import { FilterIcon } from "lucide-react";
import { useMediaQuery } from "usehooks-ts";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
  Button,
  Checkbox,
  ScrollArea,
  SearchInput,
  Separator,
  Sheet,
  SheetContent,
  SheetTrigger,
  V2Label,
} from "web-design-system";

export type Filter = {
  title: string;
  options: FilterOption[];
};

export type FilterOption = {
  id: string;
  label: string;
  count?: number;
  checked: boolean;
  onCheckedChange: (id: string) => void;
};

export type FilterListProps = {
  onClearAll: () => void;
  showSearch?: boolean;
  searchValue?: string;
  onSearchChange?: (value: string) => void;
  searchPlaceholder?: string;
  filters: Filter[];
  showAccordion?: boolean;
  /** Filter titles to expand initially. Defaults to all filters open. */
  defaultOpenFilters?: string[];
};

function FilterContainer({ children }: { children: React.ReactNode }) {
  const isDesktop = useMediaQuery("(min-width: 1024px)");

  if (isDesktop) {
    return (
      <div className="flex h-full w-70 flex-shrink-0">
        <ScrollArea className="flex-1 flex flex-col border rounded-lg bg-card">
          <div className="flex size-full flex-col px-4">{children}</div>
        </ScrollArea>
      </div>
    );
  }

  return (
    <Sheet>
      <SheetTrigger asChild>
        <Button size="icon" variant="outline">
          <FilterIcon />
        </Button>
      </SheetTrigger>
      <SheetContent className="p-0 w-[300px]">
        <div className="flex size-full flex-col px-4 bg-card overflow-y-auto">
          {children}
        </div>
      </SheetContent>
    </Sheet>
  );
}

function FilterOption({
  option,
  title,
}: {
  option: FilterOption;
  title: string;
}) {
  return (
    <div className="flex gap-3 items-center">
      <Checkbox
        id={`${title}_${option.id}`}
        checked={option.checked}
        onCheckedChange={() => option.onCheckedChange(option.id)}
      />
      <V2Label
        className="font-medium text-sm"
        htmlFor={`${title}_${option.id}`}
      >
        {option.label}
      </V2Label>
      {option.count != null ? (
        <span className="ml-auto text-sm font-medium">{option.count}</span>
      ) : null}
    </div>
  );
}

export function FilterList({
  onClearAll,
  showSearch = false,
  searchValue = "",
  onSearchChange,
  searchPlaceholder = "Search filters",
  filters,
  showAccordion = true,
  defaultOpenFilters,
}: FilterListProps) {
  return (
    <FilterContainer>
      <div className="sticky top-0 z-10 flex flex-col gap-3 bg-card py-4">
        <div className="flex items-center justify-between">
          <div className="text-sm font-semibold">Filters</div>
          <Button
            size="sm"
            variant="link"
            className="p-0 h-auto text-sm font-semibold"
            onClick={onClearAll}
          >
            Clear all
          </Button>
        </div>
        {showSearch ? (
          <SearchInput
            className="w-full"
            value={searchValue ?? ""}
            onChange={(event) => onSearchChange?.(event.target.value)}
            placeholder={searchPlaceholder}
          />
        ) : (
          <Separator />
        )}
      </div>
      {showAccordion ? (
        <Accordion
          type="multiple"
          defaultValue={
            defaultOpenFilters ?? filters.map((filter) => filter.title)
          }
          className="flex flex-col gap-4"
        >
          {filters.map((filter) => (
            <AccordionItem
              key={filter.title}
              value={filter.title}
              className="flex flex-col gap-4 border-none"
            >
              <AccordionTrigger className="py-0 text-sm font-semibold hover:no-underline tracking-[0px]">
                {filter.title}
              </AccordionTrigger>
              <AccordionContent className="pb-3 pt-0">
                <div className="flex flex-col gap-3">
                  {filter.options.map((option) => (
                    <FilterOption
                      key={option.id}
                      option={option}
                      title={filter.title}
                    />
                  ))}
                </div>
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      ) : (
        <div className="flex flex-col gap-4">
          {filters.map((filter) => (
            <div key={filter.title}>
              <div className="flex flex-col gap-3">
                {filter.options.map((option) => (
                  <FilterOption
                    key={option.id}
                    option={option}
                    title={filter.title}
                  />
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </FilterContainer>
  );
}
